import { FormatKey } from "./types"

export type ParsedSnippet = {
  description: string
  tabTrigger: string
  snippet: string
  format: FormatKey
  scope?: string
}

function unescapeVSCode(str: string): string {
  return str
    .replace(/\\\\\$/g, "$")
    .replace(/\\"/g, '"')
    .replace(/\\\\/g, "\\")
}

function unescapeSublime(str: string): string {
  return str.replace(/\\(\$)([a-z(]+)([^$])/gi, "$1$2$3")
}

function unescapeJupyter(str: string): string {
  return str.replace(/\\"/g, '"').replace(/\\\\/g, "\\")
}

export function parseExistingSnippet(input: string): ParsedSnippet | null {
  const trimmed = input.trim()

  const vscodeMatch = trimmed.match(
    /"([^"]+)":\s*\{\s*"prefix":\s*"([^"]+)",(?:\s*"scope":\s*"([^"]+)",)?\s*"body":\s*\[([\s\S]*?)\],\s*"description":\s*"([^"]+)"\s*\}/
  )
  if (vscodeMatch) {
    const bodyLines = vscodeMatch[4]
      .split("\n")
      .map((line) => line.trim())
      .filter((line) => line.length > 0)
      .map((line) => line.replace(/^"|",?$|"$/g, ""))
      .map(unescapeVSCode)
      .join("\n")

    return {
      description: vscodeMatch[1] || vscodeMatch[5] || "",
      tabTrigger: vscodeMatch[2] || "",
      snippet: bodyLines,
      format: FormatKey.VSCODE,
      scope: vscodeMatch[3] || undefined,
    }
  }

  const sublimeMatch = trimmed.match(
    /<snippet>[\s\S]*?<content><!\[CDATA\[([\s\S]*?)\]\]><\/content>[\s\S]*?<tabTrigger>([^<]+)<\/tabTrigger>[\s\S]*?<description>([^<]+)<\/description>[\s\S]*?<\/snippet>/
  )
  if (sublimeMatch) {
    return {
      description: sublimeMatch[3]?.trim() || "",
      tabTrigger: sublimeMatch[2]?.trim() || "",
      snippet: unescapeSublime(sublimeMatch[1]?.trim() || ""),
      format: FormatKey.SUBLIME,
    }
  }

  const atomMatch = trimmed.match(
    /'([^']+)':\s*'prefix':\s*'([^']+)'\s*'body':\s*"""\s*([\s\S]*?)\s*"""/
  )
  if (atomMatch) {
    return {
      description: atomMatch[1] || "",
      tabTrigger: atomMatch[2] || "",
      snippet: atomMatch[3]?.trim() || "",
      format: FormatKey.ATOM,
    }
  }

  const jupyterMatch = trimmed.match(
    /\{\s*"snippets":\s*\[\s*\{\s*"name":\s*"([^"]+)",\s*"code":\s*\[([\s\S]*?)\]\s*\}\s*\]\s*\}/
  )
  if (jupyterMatch) {
    const codeLines = jupyterMatch[2]
      .split("\n")
      .map((line) => line.trim())
      .filter((line) => line.length > 0)
      .map((line) => line.replace(/^"|",?$|"$/g, ""))
      .map(unescapeJupyter)
      .join("\n")

    return {
      description: jupyterMatch[1] || "",
      tabTrigger: "",
      snippet: codeLines,
      format: FormatKey.JUPYTER,
    }
  }

  try {
    const json = JSON.parse(trimmed)
    const keys = Object.keys(json)
    if (keys.length > 0) {
      const firstKey = keys[0]
      const snippetData = json[firstKey]
      if (
        snippetData &&
        typeof snippetData === "object" &&
        "prefix" in snippetData &&
        "body" in snippetData
      ) {
        const body = Array.isArray(snippetData.body)
          ? snippetData.body.join("\n")
          : String(snippetData.body || "")
        return {
          description: snippetData.description || firstKey || "",
          tabTrigger: String(snippetData.prefix || ""),
          snippet: body,
          format: FormatKey.VSCODE,
          scope:
            typeof snippetData.scope === "string"
              ? snippetData.scope
              : undefined,
        }
      }
    }
  } catch {}

  return null
}
